import {
  DeleteOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Table,
  Tooltip,
  message,
} from 'antd';
import { useEffect, useState } from 'react';
import type { CurrentUser } from '@cell-bank/shared';
import { useTranslation } from 'react-i18next';

type Definition = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  dataType: 'TEXT' | 'NUMBER' | 'BOOLEAN' | 'SELECT';
  unit: string | null;
  options: string[];
};
type TemplateItem = { definitionId: string; code: string };
type Template = {
  id: string;
  code: string;
  nameZh: string | null;
  nameEn: string | null;
  items: TemplateItem[];
};
type ConfigKind = 'definition' | 'template';

export function QualitySettingsPage({
  templatesEnabled,
}: {
  templatesEnabled: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [definitions, setDefinitions] = useState<Definition[]>([]);
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState(false);
  const [configKind, setConfigKind] = useState<ConfigKind>();
  const [currentUser, setCurrentUser] = useState<CurrentUser>();
  const [configForm] = Form.useForm();
  const [messageApi, holder] = message.useMessage();
  const canManage = currentUser?.permissions.includes('qc:manage') ?? false;
  const title = (item: { nameZh: string | null; nameEn: string | null }) =>
    i18n.resolvedLanguage === 'en-US'
      ? (item.nameEn ?? item.nameZh)
      : (item.nameZh ?? item.nameEn);

  const load = async () => {
    setLoading(true);
    try {
      const [userResponse, definitionResponse, templateResponse] =
        await Promise.all([
          fetch('/api/v1/auth/me', { credentials: 'include' }),
          fetch('/api/v1/qc-definitions', { credentials: 'include' }),
          fetch('/api/v1/qc-templates', { credentials: 'include' }),
        ]);
      if (!userResponse.ok || !definitionResponse.ok || !templateResponse.ok)
        throw new Error();
      setCurrentUser((await userResponse.json()) as CurrentUser);
      setDefinitions(
        ((await definitionResponse.json()) as { items: Definition[] }).items,
      );
      setTemplates(
        ((await templateResponse.json()) as { items: Template[] }).items,
      );
    } catch {
      await messageApi.error(t('quality.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const deleteTemplate = async (template: Template) => {
    const response = await fetch(`/api/v1/qc-templates/${template.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      await messageApi.error(t('quality.templateDeleteFailed'));
      return;
    }
    await load();
    await messageApi.success(t('quality.templateDeleted'));
  };

  const deleteDefinition = async (definition: Definition) => {
    const response = await fetch(`/api/v1/qc-definitions/${definition.id}`, {
      method: 'DELETE',
      credentials: 'include',
    });
    if (!response.ok) {
      await messageApi.error(t('quality.definitionDeleteFailed'));
      return;
    }
    await load();
    await messageApi.success(t('quality.definitionDeleted'));
  };

  useEffect(() => {
    void load();
  }, []);

  const createConfig = async (values: Record<string, unknown>) => {
    if (!configKind) return;
    const path =
      configKind === 'definition' ? 'qc-definitions' : 'qc-templates';
    const body =
      configKind === 'definition'
        ? {
            ...values,
            options:
              typeof values.options === 'string'
                ? values.options
                    .split(',')
                    .map((item) => item.trim())
                    .filter(Boolean)
                : undefined,
          }
        : {
            ...values,
            items: ((values.definitionIds as string[] | undefined) ?? []).map(
              (definitionId) => ({ definitionId, isRequired: true }),
            ),
          };
    const response = await fetch(`/api/v1/${path}`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) {
      await messageApi.error(t('quality.saveFailed'));
      return;
    }
    setConfigKind(undefined);
    configForm.resetFields();
    await load();
    await messageApi.success(t('quality.saved'));
  };

  return (
    <section>
      {holder}
      <Row gutter={[16, 16]}>
        <Col span={24}>
          <Card
            title={t('quality.definitions')}
            extra={
              <Space>
                <Tooltip title={t('quality.refresh')}>
                  <Button
                    aria-label={t('quality.refresh')}
                    type="text"
                    shape="circle"
                    icon={<ReloadOutlined />}
                    loading={loading}
                    onClick={() => void load()}
                  />
                </Tooltip>
                {canManage ? (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      configForm.resetFields();
                      setConfigKind('definition');
                    }}
                  >
                    {t('quality.createDefinition')}
                  </Button>
                ) : null}
              </Space>
            }
          >
            <Table
              size="small"
              rowKey="id"
              loading={loading}
              tableLayout="fixed"
              scroll={{ x: 'max-content' }}
              pagination={false}
              dataSource={definitions}
              columns={[
                { title: t('quality.code'), dataIndex: 'code' },
                {
                  title: t('quality.name'),
                  render: (_, row: Definition) => title(row),
                },
                { title: t('quality.dataType'), dataIndex: 'dataType' },
                {
                  title: t('common.actions'),
                  width: 90,
                  render: (_, row: Definition) =>
                    canManage ? (
                      <Popconfirm
                        title={t('quality.definitionDeleteConfirm', {
                          name: title(row) ?? row.code,
                        })}
                        okText={t('common.confirm')}
                        cancelText={t('common.cancel')}
                        onConfirm={() => void deleteDefinition(row)}
                      >
                        <Button danger type="link" icon={<DeleteOutlined />}>
                          {t('common.delete')}
                        </Button>
                      </Popconfirm>
                    ) : null,
                },
              ]}
            />
          </Card>
        </Col>
        {templatesEnabled && (
          <Col span={24}>
            <Card
              title={t('quality.templates')}
              extra={
                canManage ? (
                  <Button
                    type="link"
                    icon={<PlusOutlined />}
                    onClick={() => {
                      configForm.resetFields();
                      setConfigKind('template');
                    }}
                  >
                    {t('quality.createTemplate')}
                  </Button>
                ) : null
              }
            >
              <Table
                size="small"
                rowKey="id"
                loading={loading}
                tableLayout="fixed"
                scroll={{ x: 'max-content' }}
                pagination={false}
                dataSource={templates}
                columns={[
                  { title: t('quality.code'), dataIndex: 'code' },
                  {
                    title: t('quality.name'),
                    render: (_, row: Template) => title(row),
                  },
                  {
                    title: t('quality.items'),
                    render: (_, row: Template) =>
                      row.items.map((item) => item.code).join(', '),
                  },
                  {
                    title: t('common.actions'),
                    width: 90,
                    render: (_, row: Template) =>
                      canManage ? (
                        <Popconfirm
                          title={t('quality.templateDeleteConfirm', {
                            name: title(row) ?? row.code,
                          })}
                          okText={t('common.confirm')}
                          cancelText={t('common.cancel')}
                          onConfirm={() => void deleteTemplate(row)}
                        >
                          <Button danger type="link" icon={<DeleteOutlined />}>
                            {t('common.delete')}
                          </Button>
                        </Popconfirm>
                      ) : null,
                  },
                ]}
              />
            </Card>
          </Col>
        )}
      </Row>
      <Modal
        open={!!configKind}
        title={
          configKind === 'definition'
            ? t('quality.createDefinition')
            : t('quality.createTemplate')
        }
        okText={t('common.save')}
        cancelText={t('common.cancel')}
        onCancel={() => setConfigKind(undefined)}
        onOk={() => void configForm.submit()}
      >
        <Form<Record<string, unknown>>
          form={configForm}
          layout="vertical"
          onFinish={(values) => void createConfig(values)}
        >
          <Form.Item
            name="code"
            label={
              <Space size={4}>
                <span>{t('quality.code')}</span>
                <Tooltip
                  title={t(
                    configKind === 'definition'
                      ? 'quality.definitionCodeHint'
                      : 'quality.templateCodeHint',
                  )}
                >
                  <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
                </Tooltip>
              </Space>
            }
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="nameZh"
            label={t('quality.nameZh')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="nameEn" label={t('quality.nameEn')}>
            <Input />
          </Form.Item>
          {configKind === 'definition' ? (
            <>
              <Form.Item
                name="dataType"
                label={t('quality.dataType')}
                rules={[{ required: true }]}
              >
                <Select
                  options={['TEXT', 'NUMBER', 'BOOLEAN', 'SELECT'].map(
                    (value) => ({ value, label: value }),
                  )}
                />
              </Form.Item>
              <Form.Item name="unit" label={t('quality.unit')}>
                <Input />
              </Form.Item>
              <Form.Item
                name="passExpression"
                label={t('quality.passExpression')}
              >
                <Input />
              </Form.Item>
              <Form.Item name="options" label={t('quality.options')}>
                <Input placeholder="A, B, C" />
              </Form.Item>
            </>
          ) : (
            <>
              <Form.Item name="description" label={t('quality.description')}>
                <Input />
              </Form.Item>
              <Form.Item
                name="definitionIds"
                label={t('quality.definitions')}
                rules={[{ required: true, type: 'array', min: 1 }]}
              >
                <Select
                  mode="multiple"
                  showSearch
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  placeholder={t('quality.selectDefinitions')}
                  options={definitions.map((item) => ({
                    value: item.id,
                    label: `${item.code} — ${title(item)}`,
                  }))}
                />
              </Form.Item>
            </>
          )}
        </Form>
      </Modal>
    </section>
  );
}
